import { useCallback, useEffect, useRef, useState } from 'react';
import type { GridSortModel } from '@mui/x-data-grid';

type UseGridSortStateResult = {
  sortModel: GridSortModel;
  setSortModel: (model: GridSortModel) => void;
  handleSortModelChange: (model: GridSortModel) => void;
};

const normaliseSortModel = (model: GridSortModel): GridSortModel => {
  if (model.length <= 1) return model;
  return [model[0]];
};

const areSortModelsEqual = (left: GridSortModel, right: GridSortModel): boolean => {
  if (left.length !== right.length) return false;
  return left.every(
    (item, index) => item.field === right[index]?.field && item.sort === right[index]?.sort,
  );
};

export const useGridSortState = (initialSortModel: GridSortModel): UseGridSortStateResult => {
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel);
  const sortModelRef = useRef(sortModel);
  const pendingSortModelRef = useRef<GridSortModel | null>(null);
  const flushScheduledRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    sortModelRef.current = sortModel;
  }, [sortModel]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const commitSortModel = useCallback((model: GridSortModel): void => {
    const normalisedModel = normaliseSortModel(model);
    if (areSortModelsEqual(sortModelRef.current, normalisedModel)) return;
    pendingSortModelRef.current = normalisedModel;
    if (flushScheduledRef.current) return;
    flushScheduledRef.current = true;
    const flush = (): void => {
      flushScheduledRef.current = false;
      if (!isMountedRef.current) return;
      const pendingModel = pendingSortModelRef.current;
      pendingSortModelRef.current = null;
      if (!pendingModel) return;
      setSortModel((currentModel) =>
        areSortModelsEqual(currentModel, pendingModel) ? currentModel : pendingModel,
      );
    };
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(flush);
      return;
    }
    window.setTimeout(flush, 0);
  }, []);

  return {
    sortModel,
    setSortModel: commitSortModel,
    handleSortModelChange: commitSortModel,
  };
};
